import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { API_URL } from '../config/api';
import MapComponent from '../MapComponent';
import { Search } from 'lucide-react';
import './ComparablesTab.css';

const LISTING_TYPES = ['for_sale', 'sold', 'pending'];
const LISTING_TYPE_LABELS = { for_sale: 'For Sale', sold: 'Sold', pending: 'Pending' };

const ComparablesTab = ({ portfolioId }) => {
    const { session } = useAuth();

    // Filter state
    const [listingTypes, setListingTypes] = useState(['for_sale', 'sold']);
    const [radius, setRadius] = useState(1);
    const [pastDays, setPastDays] = useState(180);
    const [mlsOnly, setMlsOnly] = useState(false);
    const [bedsTol, setBedsTol] = useState(1);
    const [bathsTol, setBathsTol] = useState(1);
    const [sqftPct, setSqftPct] = useState(0.2);

    const queryParams = useMemo(() => {
        const p = new URLSearchParams();
        listingTypes.forEach(lt => p.append('listing_type', lt));
        p.set('radius_mi', radius);
        p.set('past_days', pastDays);
        p.set('mls_only', mlsOnly);
        p.set('beds_tol', bedsTol);
        p.set('baths_tol', bathsTol);
        p.set('sqft_pct', sqftPct);
        return p.toString();
    }, [listingTypes, radius, pastDays, mlsOnly, bedsTol, bathsTol, sqftPct]);

    const { data, isLoading, error } = useQuery({
        queryKey: ['portfolio-comparables', portfolioId, queryParams],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/portfolio/${portfolioId}/comparables?${queryParams}`, {
                headers: { Authorization: `Bearer ${session?.access_token}` },
            });
            if (!res.ok) throw new Error('Failed to fetch comparables');
            return res.json();
        },
        enabled: !!session?.access_token && listingTypes.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const toggleListingType = useCallback((lt) => {
        setListingTypes(prev => {
            if (prev.includes(lt)) {
                return prev.length > 1 ? prev.filter(t => t !== lt) : prev;
            }
            return [...prev, lt];
        });
    }, []);

    const items = data?.items || [];
    const stats = data?.stats || {};
    const subject = data?.subject || {};

    // Map markers
    const mapMarkers = useMemo(() => {
        const markers = items
            .filter(i => i.latitude && i.longitude)
            .map(i => ({
                property_id: i.property_id || Math.random(),
                latitude: i.latitude,
                longitude: i.longitude,
                address: i.address || '',
                list_price: i.sold_price || i.list_price,
                beds: i.beds,
                baths: i.baths,
            }));

        if (subject.latitude && subject.longitude) {
            markers.unshift({
                property_id: 'subject',
                latitude: subject.latitude,
                longitude: subject.longitude,
                address: `Subject: ${subject.address || ''}`,
                list_price: null,
                isSubject: true,
            });
        }
        return markers;
    }, [items, subject]);

    return (
        <div className="comp-tab">
            {/* Filters */}
            <div className="comp-filters">
                <div className="comp-filter-row">
                    <div className="comp-filter-group">
                        <label className="comp-filter-label">Listing Type</label>
                        <div className="comp-chips">
                            {LISTING_TYPES.map(lt => (
                                <button
                                    key={lt}
                                    className={`comp-chip ${listingTypes.includes(lt) ? 'active' : ''}`}
                                    onClick={() => toggleListingType(lt)}
                                >
                                    {LISTING_TYPE_LABELS[lt]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="comp-filter-group comp-filter-md">
                        <label className="comp-filter-label">Radius (miles)</label>
                        <input
                            type="number"
                            className="comp-input"
                            value={radius}
                            onChange={(e) => setRadius(Math.max(0.1, Number(e.target.value)))}
                            min="0.1"
                            max="25"
                            step="0.5"
                        />
                    </div>
                    <div className="comp-filter-group comp-filter-md">
                        <label className="comp-filter-label">Past Days</label>
                        <input
                            type="number"
                            className="comp-input"
                            value={pastDays}
                            onChange={(e) => setPastDays(Math.max(7, Number(e.target.value)))}
                            min="7"
                            max="1095"
                        />
                    </div>
                    <div className="comp-filter-group">
                        <label className="comp-filter-label">&nbsp;</label>
                        <label className="comp-toggle-label">
                            <input
                                type="checkbox"
                                checked={mlsOnly}
                                onChange={(e) => setMlsOnly(e.target.checked)}
                            />
                            MLS Only
                        </label>
                    </div>
                </div>
                <div className="comp-filter-row comp-filter-row-secondary">
                    <div className="comp-filter-group comp-filter-md">
                        <label className="comp-filter-label">Beds ±</label>
                        <input
                            type="number"
                            className="comp-input"
                            value={bedsTol}
                            onChange={(e) => setBedsTol(Math.max(0, Number(e.target.value)))}
                            min="0"
                            max="5"
                        />
                    </div>
                    <div className="comp-filter-group comp-filter-md">
                        <label className="comp-filter-label">Baths ±</label>
                        <input
                            type="number"
                            className="comp-input"
                            value={bathsTol}
                            onChange={(e) => setBathsTol(Math.max(0, Number(e.target.value)))}
                            min="0"
                            max="5"
                        />
                    </div>
                    <div className="comp-filter-group comp-filter-md">
                        <label className="comp-filter-label">Size ±%</label>
                        <input
                            type="number"
                            className="comp-input"
                            value={Math.round(sqftPct * 100)}
                            onChange={(e) => setSqftPct(Math.max(5, Number(e.target.value)) / 100)}
                            min="5"
                            max="100"
                        />
                    </div>
                </div>
            </div>

            {/* Stats */}
            {!isLoading && items.length > 0 && (
                <div className="comp-stats">
                    <span className="comp-stat"><strong>{stats.count}</strong> comparables</span>
                    <span className="comp-stat">Avg: <strong>{formatCurrency(stats.avg_price)}</strong></span>
                    {stats.median_price_per_sqft && (
                        <span className="comp-stat">Median $/sqft: <strong>${Math.round(stats.median_price_per_sqft)}</strong></span>
                    )}
                    <span className="comp-stat">Range: {formatCurrency(stats.min_price)} – {formatCurrency(stats.max_price)}</span>
                </div>
            )}

            {/* Content: map + table */}
            {isLoading ? (
                <div className="comp-loading"><div className="spinner"></div></div>
            ) : error ? (
                <div className="comp-error">Failed to load comparables. Try adjusting your filters.</div>
            ) : items.length === 0 ? (
                <div className="comp-empty">
                    <Search size={36} strokeWidth={1.5} />
                    <p>No comparables found. Try increasing the radius or past days.</p>
                </div>
            ) : (
                <div className="comp-content">
                    <div className="comp-map-wrap">
                        <MapComponent
                            properties={mapMarkers}
                            center={subject.latitude && subject.longitude ? [subject.latitude, subject.longitude] : undefined}
                        />
                    </div>
                    <div className="comp-table-wrap">
                        <table className="comp-table">
                            <thead>
                                <tr>
                                    <th>Address</th>
                                    <th>Status</th>
                                    <th>Price</th>
                                    <th>Beds</th>
                                    <th>Baths</th>
                                    <th>Sqft</th>
                                    <th>$/Sqft</th>
                                    <th>Distance</th>
                                    <th>DOM</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => (
                                    <tr key={item.property_id || i}>
                                        <td className="comp-cell-address">{item.address || '—'}</td>
                                        <td>
                                            <span className={`comp-status-chip comp-status-${item.listing_type}`}>
                                                {LISTING_TYPE_LABELS[item.listing_type] || item.listing_type}
                                            </span>
                                        </td>
                                        <td>{formatCurrency(item.sold_price || item.list_price)}</td>
                                        <td>{item.beds ?? '—'}</td>
                                        <td>{item.baths ?? '—'}</td>
                                        <td>{item.sqft ? Number(item.sqft).toLocaleString() : '—'}</td>
                                        <td>{item.price_per_sqft ? `$${Math.round(item.price_per_sqft)}` : '—'}</td>
                                        <td>{item.distance_miles != null ? `${item.distance_miles} mi` : '—'}</td>
                                        <td>{item.days_on_market ?? '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComparablesTab;
